(function (root) {
  "use strict";

  var MIN_FREQUENCY = 16;
  var MAX_FREQUENCY = 20000;
  var MIN_REFERENCE = 400;
  var MAX_REFERENCE = 480;
  var REFERENCE_DECIMALS = 2;
  var MIN_SEMITONES = -48;
  var MAX_SEMITONES = 48;
  var MIN_CENTS = -100;
  var MAX_CENTS = 100;
  var MIN_MIDI = 0;
  var MAX_MIDI = 127;
  var MAX_HARMONICS = 32;
  var A4_MIDI = 69;
  var LOG2_MIN_POSITIVE = -1074; // log2(Number.MIN_VALUE), the smallest positive double.
  var MINUS = "−";
  var NOTATIONS = Object.freeze(["sharp", "flat"]);
  var NOTE_NAMES = Object.freeze({
    sharp: Object.freeze(["C", "C♯", "D", "D♯", "E", "F", "F♯", "G", "G♯", "A", "A♯", "B"]),
    flat: Object.freeze(["C", "D♭", "D", "E♭", "E", "F", "G♭", "G", "A♭", "A", "B♭", "B"])
  });
  var ASCII_NOTE_NAMES = {
    sharp: ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"],
    flat: ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"]
  };
  var SPOKEN_NOTE_NAMES = {
    sharp: ["C", "C sharp", "D", "D sharp", "E", "F", "F sharp", "G", "G sharp", "A", "A sharp", "B"],
    flat: ["C", "D flat", "D", "E flat", "E", "F", "G flat", "G", "A flat", "A", "B flat", "B"]
  };
  var BLACK_KEYS = Object.freeze([1, 3, 6, 8, 10]);
  var TABLE_OCTAVES = Object.freeze([0, 1, 2, 3, 4, 5, 6, 7, 8]);
  var HARMONIC_COUNTS = Object.freeze([8, 12, 16, 24, 32]);

  function failure(code, message) {
    return { ok: false, error: { code: code, message: message } };
  }
  function success(value) { return Object.assign({ ok: true }, value); }
  function finite(value) { return typeof value === "number" && Number.isFinite(value); }
  function isInput(value) { return !!value && typeof value === "object" && !Array.isArray(value); }
  // Turns -0 into 0 so no result ever displays as "-0".
  function noNegativeZero(value) { return value === 0 ? 0 : value; }

  function validateEnteredFrequency(frequency) {
    if (!finite(frequency) || frequency <= 0) return failure("invalid_frequency", "Frequency must be a finite positive number of hertz.");
    if (frequency < MIN_FREQUENCY || frequency > MAX_FREQUENCY) {
      return failure("frequency_out_of_range", "Frequency must be between 16 and 20,000 Hz.");
    }
    return success({ frequency: frequency });
  }
  function validateDerivedFrequency(frequency) {
    if (!finite(frequency) || frequency <= 0) return failure("invalid_frequency", "Frequency must be a finite positive number of hertz.");
    return success({ frequency: frequency });
  }
  function validateReference(referenceA4) {
    if (!finite(referenceA4) || referenceA4 <= 0) return failure("invalid_reference", "A4 reference must be a finite positive number of hertz.");
    if (referenceA4 < MIN_REFERENCE || referenceA4 > MAX_REFERENCE) {
      return failure("reference_out_of_range", "A4 reference must be between 400 and 480 Hz.");
    }
    var scaled = referenceA4 * 100;
    if (Math.abs(scaled - Math.round(scaled)) > 1e-6) {
      return failure("reference_precision", "A4 reference can have at most two decimal places.");
    }
    return success({ referenceA4: referenceA4 });
  }
  function validateNotation(notation) {
    if (NOTATIONS.indexOf(notation) === -1) return failure("invalid_notation", "Notation must be \"sharp\" or \"flat\".");
    return success({ notation: notation });
  }
  function validateReferenceAndNotation(referenceA4, notation) {
    var reference = validateReference(referenceA4);
    if (!reference.ok) return reference;
    return validateNotation(notation);
  }

  function rangeWarnings(frequency) {
    var warnings = [];
    if (frequency < MIN_FREQUENCY) warnings.push("below_supported_range");
    else if (frequency > MAX_FREQUENCY) warnings.push("above_supported_range");
    return warnings;
  }

  // Pitch class and octave for any integer MIDI value, including negatives:
  // JavaScript's % keeps the dividend's sign, so the modulo is applied twice.
  function noteParts(midi) {
    return {
      pitchClass: noNegativeZero(((midi % 12) + 12) % 12),
      octave: Math.floor(midi / 12) - 1
    };
  }
  function octaveText(octave, minus) {
    return octave < 0 ? minus + Math.abs(octave) : String(octave);
  }
  function namesFor(midi, notation) {
    var parts = noteParts(midi);
    var pitchClass = parts.pitchClass;
    var octave = parts.octave;
    var other = notation === "sharp" ? "flat" : "sharp";
    var spokenOctave = octave < 0 ? ", octave minus " + Math.abs(octave) : " " + octave;
    return {
      name: NOTE_NAMES[notation][pitchClass] + octaveText(octave, MINUS),
      asciiName: ASCII_NOTE_NAMES[notation][pitchClass] + octaveText(octave, "-"),
      spokenName: SPOKEN_NOTE_NAMES[notation][pitchClass] + spokenOctave,
      enharmonicName: BLACK_KEYS.indexOf(pitchClass) === -1 ? null : NOTE_NAMES[other][pitchClass] + octaveText(octave, MINUS),
      pitchClass: pitchClass,
      octave: octave,
      inMidiRange: midi >= MIN_MIDI && midi <= MAX_MIDI
    };
  }
  /**
   * A · 2^((midi − 69) / 12), as { frequency, status }. status is "ok", or
   * "underflow" / "overflow" (frequency null) when the exact value lies below
   * Number.MIN_VALUE or above Number.MAX_VALUE. The power of two is split into
   * a fractional part and two exact power-of-two scalings so no intermediate
   * value underflows or overflows before the final result.
   */
  function frequencyForMidi(midi, referenceA4) {
    var exponent = (midi - A4_MIDI) / 12;
    if (Math.log2(referenceA4) + exponent < LOG2_MIN_POSITIVE) return { frequency: null, status: "underflow" };
    var whole = Math.floor(exponent);
    var firstStep = Math.max(-1000, Math.min(1000, whole));
    var frequency = referenceA4 * Math.pow(2, exponent - whole) * Math.pow(2, firstStep) * Math.pow(2, whole - firstStep);
    if (frequency === 0) return { frequency: null, status: "underflow" };
    if (!Number.isFinite(frequency)) return { frequency: null, status: "overflow" };
    return { frequency: frequency, status: "ok" };
  }

  function noteName(midi, notation) {
    if (!Number.isInteger(midi)) return failure("invalid_midi", "MIDI note must be an integer.");
    var notationCheck = validateNotation(notation);
    if (!notationCheck.ok) return notationCheck;
    return success(namesFor(midi, notation));
  }

  // Any integer is accepted, but a frequency that is not a representable
  // positive finite number fails with frequency_not_representable and
  // error.reason "underflow" or "overflow".
  function midiToFrequency(midi, referenceA4) {
    if (!Number.isInteger(midi)) return failure("invalid_midi", "MIDI note must be an integer.");
    var reference = validateReference(referenceA4);
    if (!reference.ok) return reference;
    var result = frequencyForMidi(midi, referenceA4);
    if (result.status !== "ok") {
      var notRepresentable = failure("frequency_not_representable", "The frequency of this MIDI note is " +
        (result.status === "underflow" ? "smaller than the smallest" : "larger than the largest") + " positive number JavaScript can represent.");
      notRepresentable.error.reason = result.status;
      return notRepresentable;
    }
    return success({ midiNote: midi, frequency: result.frequency });
  }

  /**
   * Accepts any finite positive frequency, including derived values outside
   * the 16–20,000 Hz input range; those are returned with range flags.
   * The exact MIDI value is quantised to 0.1 cent before choosing the nearest
   * note, so exact midpoints round up and cents always lie in [-50.0, +49.9].
   * Logarithms are taken separately (log2 f − log2 A) so extreme frequencies
   * never underflow in a division. When the nearest note's own frequency is
   * not representable, nearestFrequency is null and nearestFrequencyStatus
   * says "underflow" or "overflow"; every other field stays valid.
   */
  function frequencyToPitch(input) {
    if (!isInput(input)) return failure("invalid_input", "Input must be an object.");
    var frequencyCheck = validateDerivedFrequency(input.frequency);
    if (!frequencyCheck.ok) return frequencyCheck;
    var settings = validateReferenceAndNotation(input.referenceA4, input.notation);
    if (!settings.ok) return settings;

    var midiExact = A4_MIDI + 12 * (Math.log2(input.frequency) - Math.log2(input.referenceA4));
    var quantised = noNegativeZero(Math.round(midiExact * 1000) / 1000);
    var nearest = Math.floor(quantised + 0.5);
    var cents = noNegativeZero(Math.round((quantised - nearest) * 1000) / 10);
    var names = namesFor(nearest, input.notation);
    var nearestNote = frequencyForMidi(nearest, input.referenceA4);
    var warnings = rangeWarnings(input.frequency);
    if (!names.inMidiRange) warnings.push("outside_midi_range");

    return success({
      frequency: input.frequency,
      referenceA4: input.referenceA4,
      notation: input.notation,
      midiExact: midiExact,
      decimalPitch: quantised,
      midiNote: nearest,
      pitchClass: names.pitchClass,
      octave: names.octave,
      name: names.name,
      asciiName: names.asciiName,
      spokenName: names.spokenName,
      enharmonicName: names.enharmonicName,
      cents: cents,
      direction: cents > 0 ? "above" : (cents < 0 ? "below" : "exact"),
      nearestFrequency: nearestNote.frequency,
      nearestFrequencyStatus: nearestNote.status,
      inSupportedRange: warnings.indexOf("below_supported_range") === -1 && warnings.indexOf("above_supported_range") === -1,
      inMidiRange: names.inMidiRange,
      rangeWarnings: warnings
    });
  }

  function noteToFrequency(input) {
    if (!isInput(input)) return failure("invalid_input", "Input must be an object.");
    if (!Number.isInteger(input.pitchClass) || input.pitchClass < 0 || input.pitchClass > 11) {
      return failure("invalid_pitch_class", "Pitch class must be an integer from 0 (C) to 11 (B).");
    }
    if (!Number.isInteger(input.octave) || TABLE_OCTAVES.indexOf(input.octave) === -1) {
      return failure("invalid_octave", "Octave must be an integer from 0 to 8.");
    }
    var settings = validateReferenceAndNotation(input.referenceA4, input.notation);
    if (!settings.ok) return settings;

    var midi = 12 * (input.octave + 1) + input.pitchClass;
    var names = namesFor(midi, input.notation);
    return success({
      pitchClass: input.pitchClass,
      octave: input.octave,
      referenceA4: input.referenceA4,
      notation: input.notation,
      midiNote: midi,
      frequency: frequencyForMidi(midi, input.referenceA4).frequency,
      name: names.name,
      asciiName: names.asciiName,
      spokenName: names.spokenName,
      enharmonicName: names.enharmonicName
    });
  }

  function pitchShift(input) {
    if (!isInput(input)) return failure("invalid_input", "Input must be an object.");
    var frequencyCheck = validateEnteredFrequency(input.frequency);
    if (!frequencyCheck.ok) return frequencyCheck;
    if (!Number.isInteger(input.semitones)) return failure("invalid_semitones", "Semitones must be a whole number.");
    if (input.semitones < MIN_SEMITONES || input.semitones > MAX_SEMITONES) {
      return failure("semitones_out_of_range", "Semitones must be between -48 and +48.");
    }
    if (!finite(input.cents)) return failure("invalid_cents", "Cents must be a finite number.");
    if (input.cents < MIN_CENTS || input.cents > MAX_CENTS) {
      return failure("cents_out_of_range", "Cents must be between -100 and +100.");
    }

    var totalSemitones = noNegativeZero(input.semitones + input.cents / 100);
    var ratio = Math.pow(2, totalSemitones / 12);
    var shiftedFrequency = input.frequency * ratio;
    if (!finite(shiftedFrequency) || shiftedFrequency <= 0) return failure("invalid_result", "The shifted frequency is not a finite positive number.");
    var warnings = rangeWarnings(shiftedFrequency);
    return success({
      frequency: input.frequency,
      semitones: input.semitones,
      cents: noNegativeZero(input.cents),
      totalSemitones: totalSemitones,
      ratio: ratio,
      shiftedFrequency: shiftedFrequency,
      speedChangePercent: noNegativeZero((ratio - 1) * 100),
      playbackRatePercent: ratio * 100,
      lengthPercent: 100 / ratio,
      inSupportedRange: warnings.length === 0,
      rangeWarnings: warnings
    });
  }

  function harmonicSeries(input) {
    if (!isInput(input)) return failure("invalid_input", "Input must be an object.");
    var frequencyCheck = validateEnteredFrequency(input.fundamental);
    if (!frequencyCheck.ok) return frequencyCheck;
    if (!Number.isInteger(input.count) || input.count < 1 || input.count > MAX_HARMONICS) {
      return failure("invalid_count", "Harmonic count must be an integer from 1 to 32.");
    }
    var rows = [];
    for (var harmonic = 1; harmonic <= input.count; harmonic += 1) {
      var frequency = harmonic * input.fundamental;
      var warnings = rangeWarnings(frequency);
      rows.push({
        harmonic: harmonic,
        overtone: harmonic - 1,
        frequency: frequency,
        intervalSemitones: noNegativeZero(12 * Math.log2(harmonic)),
        inSupportedRange: warnings.length === 0,
        rangeWarnings: warnings
      });
    }
    return success({ fundamental: input.fundamental, count: input.count, rows: rows });
  }

  /**
   * 1/n weights normalised over the given harmonic numbers. Results keep the
   * input order, so callers must match gains by `harmonic`, not by index.
   */
  function harmonicGains(harmonics) {
    if (!Array.isArray(harmonics) || harmonics.length === 0) {
      return failure("invalid_harmonics", "Harmonics must be a non-empty array of harmonic numbers.");
    }
    var seen = {};
    for (var i = 0; i < harmonics.length; i += 1) {
      var harmonic = harmonics[i];
      if (!Number.isInteger(harmonic) || harmonic < 1 || harmonic > MAX_HARMONICS) {
        return failure("invalid_harmonics", "Each harmonic number must be an integer from 1 to 32.");
      }
      if (seen[harmonic]) return failure("duplicate_harmonic", "Each harmonic number may appear only once.");
      seen[harmonic] = true;
    }
    var total = harmonics.reduce(function (sum, harmonic) { return sum + 1 / harmonic; }, 0);
    return success({
      gains: harmonics.map(function (harmonic) { return { harmonic: harmonic, gain: (1 / harmonic) / total }; })
    });
  }

  function referenceTable(input) {
    if (!isInput(input)) return failure("invalid_input", "Input must be an object.");
    var settings = validateReferenceAndNotation(input.referenceA4, input.notation);
    if (!settings.ok) return settings;
    var rows = [];
    for (var pitchClass = 0; pitchClass < 12; pitchClass += 1) {
      rows.push({
        pitchClass: pitchClass,
        name: NOTE_NAMES[input.notation][pitchClass],
        asciiName: ASCII_NOTE_NAMES[input.notation][pitchClass],
        cells: TABLE_OCTAVES.map(function (octave) {
          var midi = 12 * (octave + 1) + pitchClass;
          var names = namesFor(midi, input.notation);
          return {
            octave: octave,
            midiNote: midi,
            frequency: frequencyForMidi(midi, input.referenceA4).frequency,
            name: names.name,
            asciiName: names.asciiName
          };
        })
      });
    }
    return success({ referenceA4: input.referenceA4, notation: input.notation, octaves: TABLE_OCTAVES.slice(), rows: rows });
  }

  /**
   * DOM- and audio-independent calculator API. Every function returns
   * { ok: true, ...values } or { ok: false, error: { code, message } } and
   * never throws. Octaves use scientific pitch notation (C4 = MIDI 60).
   */
  root.AudiocolourFrequencyPitchCore = Object.freeze({
    frequencyRange: Object.freeze({ min: MIN_FREQUENCY, max: MAX_FREQUENCY }),
    referenceRange: Object.freeze({ min: MIN_REFERENCE, max: MAX_REFERENCE, decimals: REFERENCE_DECIMALS }),
    referencePresets: Object.freeze([432, 440, 442]),
    defaultReference: 440,
    semitoneRange: Object.freeze({ min: MIN_SEMITONES, max: MAX_SEMITONES }),
    centsRange: Object.freeze({ min: MIN_CENTS, max: MAX_CENTS }),
    midiRange: Object.freeze({ min: MIN_MIDI, max: MAX_MIDI }),
    notations: NOTATIONS,
    noteNames: NOTE_NAMES,
    tableOctaves: TABLE_OCTAVES,
    harmonicCounts: HARMONIC_COUNTS,
    maxHarmonics: MAX_HARMONICS,
    validateEnteredFrequency: validateEnteredFrequency,
    validateReference: validateReference,
    validateNotation: validateNotation,
    noteName: noteName,
    frequencyToPitch: frequencyToPitch,
    noteToFrequency: noteToFrequency,
    midiToFrequency: midiToFrequency,
    pitchShift: pitchShift,
    harmonicSeries: harmonicSeries,
    harmonicGains: harmonicGains,
    referenceTable: referenceTable
  });
}(globalThis));
